"use client";
import {
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import DynamicBreadcrumb from "@/app/components/elements/breadcrumb/DynamicBreadcrumb";
import ProductsHeader from "@/app/components/common/products/ProductsHeader";
import { SlugControlResponse } from "@/app/lib/features/slugControl/slugControl.types";
import useGenericProducts from "@/app/lib/features/genericProducts/genericProducts.hook";
import { GenericProductsType } from "@/app/lib/features/genericProducts/genericProducts.types";
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
  SelectedFilters,
} from "@/app/lib/features/filters/filters.types";
import useFilters from "@/app/lib/features/filters/filters.hook";
import useDebounce from "@/app/hooks/useDebounce";
import useFilterUrlSync from "@/app/hooks/useFilterUrlSync";
import ProductsLayout from "@/app/components/common/products/ProductsLayout";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import DOMPurify from "dompurify";
import { setInitialGenericProducts } from "@/app/lib/features/genericProducts/genericProducts.slice";
import { consumeBackForwardNavigation } from "@/app/lib/navigationType";
import { parseFiltersFromSearchParams } from "@/app/utils/filters.utils";
import { buildProductsRequestKey } from "@/app/lib/features/genericProducts/genericProducts.utils";
import { GenericProductsData } from "@/app/lib/features/genericProducts/genericProducts.types";
import { trackInteraction } from "@/app/lib/tracking/interactions";

const SCROLL_KEY = (slug: string) => `scroll_restore_${slug}`;

interface GenericProductsPageProps {
  slug: string;
  slugData: SlugControlResponse;
  initialDataPromise: Promise<GenericProductsData | null>;
}

const GenericProductsPage = ({
  slug,
  slugData,
  initialDataPromise,
}: GenericProductsPageProps) => {
  const initialData = use(initialDataPromise);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const {
    selected,
    userInteractionCount,
    updateSelected,
    fetchFilters,
    fetchGeneralFilters,
    resetServer,
    clearFilters,
  } = useFilters();

  const {
    products,
    isLoading,
    isPaginating,
    isSuccess,
    isError,
    pagination,
    hasMore,
    fetchMore,
    fetchProducts,
    section,
    seoTitle,
    seoCategoryTitle,
    seoCategoryText,
    ssrHydrated,
    currentSlug,
    lastRequestKey,
    reset: resetProducts,
  } = useGenericProducts();

  const [suppressOverlay, setSuppressOverlay] = useState(
    () => products.length > 0 && isSuccess,
  );

  // page.tsx SSR verisini searchParams'sız çeker (ISR cache'i query'den bağımsız),
  // yani initialData daima FİLTRESİZ/SIRALAMASIZ base listedir. URL'de filtre varsa
  // bu snapshot ne Redux'a yazılmalı ne de ilk client fetch'ini atlatmalı.
  const [hasUrlFiltersOnMount] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      Object.keys(
        parseFiltersFromSearchParams(
          new URLSearchParams(window.location.search),
        ),
      ).length > 0
    );
  });

  // Hydration bayrağı: ilk client render'ı sunucu HTML'iyle birebir aynı olmalı.
  // hasUrlFiltersOnMount sunucuda daima false olduğu için render çıktısını
  // doğrudan ona bağlamak hydration mismatch üretir.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const initialFetchSkippedRef = useRef(false);
  const lastFetchedKeyRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);
  const slugRef = useRef(slug);
  const slugTypeRef = useRef(slugData.type);
  useEffect(() => {
    slugRef.current = slug;
    slugTypeRef.current = slugData.type;
  }, [slug, slugData.type]);

  // Reset server filter state (userInteractionCount etc.) on every mount,
  // including remounts from back-navigation. Without this, a stale
  // userInteractionCount > 0 from a previous page visit causes getFilters.fulfilled
  // to skip the context merge, leaving context filters unchecked in the sidebar.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { resetServer(); }, []);

  useEffect(() => {
    const id = slugData.item_ids?.length === 1 ? slugData.item_ids[0] : null;
    if (!id) return;
    const type = slugData.type;
    if (type === "category" || type === "category_group" || type === "category_sub_group") {
      trackInteraction(type, id, "view");
    } else if (type === "brand") {
      trackInteraction("brand", id, "view");
    } else if (type === "section") {
      trackInteraction("section", id, "view");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    resetProducts();
    resetServer();
    setSuppressOverlay(false);
    initialFetchSkippedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // DOMPurify tarayıcı DOM'una bağlı: window yokken createDOMPurify erken dönüyor
  // ve `sanitize` HİÇ tanımlanmıyor (isSupported=false) — sunucuda çağrılırsa
  // TypeError ile render patlar. Bu blok SEO metni olduğu için SSR'da da basılmalı,
  // o yüzden sunucuda ham metin (projedeki diğer CMS HTML'leri gibi), client'ta
  // sanitize edilmiş hali render edilir.
  const sanitizedSeoCategoryText = useMemo(() => {
    if (!seoCategoryText) return "";
    if (typeof window === "undefined") return seoCategoryText;
    return DOMPurify.sanitize(seoCategoryText);
  }, [seoCategoryText]);

  // Kayıt her ayrılışta yazılıyor; ama YALNIZCA geri/ileri navigasyonda geri
  // yüklenmeli. Aksi hâlde menüden aynı kategoriye tekrar girildiğinde sayfa
  // eski derin scroll konumundan açılıyordu ("sayfa alttan açılıyor").
  const [restoreData] = useState<{ scrollY: number; count: number } | null>(
    () => {
      try {
        const saved = sessionStorage.getItem(SCROLL_KEY(slug));
        if (!saved) return null;
        // İleri navigasyonda da temizle — bayat kayıt sonraki geri dönüşte
        // yanlış konuma götürmesin.
        sessionStorage.removeItem(SCROLL_KEY(slug));
        if (!consumeBackForwardNavigation()) return null;
        const parsed = JSON.parse(saved);
        if (
          typeof parsed.scrollY === "number" &&
          typeof parsed.count === "number"
        )
          return parsed;
      } catch {}
      return null;
    },
  );

  const scrollRestoredRef = useRef(false);
  const productCountRef = useRef(0);
  const scrollYRef = useRef(0);

  useLayoutEffect(() => {
    productCountRef.current = products.length;
  }, [products.length]);

  useEffect(() => {
    const onScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!restoreData || scrollRestoredRef.current) return;
    if (isLoading || products.length < restoreData.count) return;
    scrollRestoredRef.current = true;
    requestAnimationFrame(() => {
      window.scrollTo({ top: restoreData.scrollY, behavior: "instant" });
    });
  }, [restoreData, products.length, isLoading]);

  useEffect(() => {
    return () => {
      const count = productCountRef.current;
      const scrollY = scrollYRef.current;
      if (count > 0) {
        try {
          sessionStorage.setItem(
            SCROLL_KEY(slug),
            JSON.stringify({ scrollY, count }),
          );
        } catch {}
      }
    };
  }, [slug]);

  const baseFilterRequest = useMemo<FilterRequest>(
    () => ({
      section_id: slugData.section_id ?? DEFAULT_FILTER_REQUEST.section_id,
      seque: slugData.seque || DEFAULT_FILTER_REQUEST.seque,
      ...(slugData.menu_type ? { menu_type: slugData.menu_type } : {}),
    }),
    [slugData.section_id, slugData.seque, slugData.menu_type],
  );

  const itemIdsKey = JSON.stringify(slugData.item_ids);

  const initialSelected = useMemo(() => {
    const result: Partial<SelectedFilters> = {};
    if (slugData.item_type && slugData.item_ids?.length) {
      result[slugData.item_type as keyof SelectedFilters] =
        slugData.item_ids as never;
    }
    if (slugData.gender_id) {
      result.gender = [slugData.gender_id];
    }
    return Object.keys(result).length ? result : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugData.item_type, itemIdsKey, slugData.gender_id]);

  const handleClearFilters = () => {
    clearFilters(initialSelected);
    setTimeout(() => {
      window.history.replaceState(null, "", pathname);
    }, 0);
  };

  const { activeFilterRequest, initReady } = useFilterUrlSync({
    selected,
    updateSelected,
    baseFilterRequest,
    initialSelected,
    interactionCount: userInteractionCount,
    // excludeFromUrl kaldırıldı: initialSelected mekanizması değişmemiş key'leri zaten URL'e yazmıyor.
    // Kullanıcı bir filtreyi değiştirince (kaldırınca/ekleyince) URL'e yazılır — SEO + UX için.
  });

  // initReady zaten dep listesinde olduğu için debouncedKey'e dahil etmeye gerek yok.
  // "init:0→1" geçişi initReady dep'i üzerinden effect'i tetikler; debouncedKey'e
  // eklemek sadece ekstra bir tetikleme üretir ve isSameRequest=false'a yol açar.
  const debouncedKey = useDebounce(
    `ui:${userInteractionCount}`,
    500,
  );

  const activeFilterRequestRef = useRef(activeFilterRequest);
  useEffect(() => {
    activeFilterRequestRef.current = activeFilterRequest;
  }, [activeFilterRequest]);

  // Fetch effect'inde stale closure'dan kaçınmak için her render'da güncellenen ref'ler
  const isSuccessRef = useRef(isSuccess);
  const isLoadingRef = useRef(isLoading);
  const productsRef = useRef(products);
  const currentSlugRef = useRef(currentSlug);
  const lastRequestKeyRef = useRef(lastRequestKey);
  useEffect(() => {
    isSuccessRef.current = isSuccess;
    isLoadingRef.current = isLoading;
    productsRef.current = products;
    currentSlugRef.current = currentSlug;
    lastRequestKeyRef.current = lastRequestKey;
  });

  useEffect(() => {
    if (!initialData) return;
    // Remount (ürün detayından geri dönüş): Redux'ta bu slug'ın verisi zaten varsa
    // SSR snapshot'ını ÜZERİNE YAZMA — kullanıcının filtreli/sıralı sonucu silinir
    // ve URL'de sort=... dururken varsayılan sıralı liste görünür.
    if (currentSlugRef.current === slug && productsRef.current.length > 0) return;
    // URL'de filtre/sıralama varken SSR base listesi yanlış veridir — Redux'a yazma;
    // aşağıdaki fetch effect'i doğru isteği atsın.
    if (hasUrlFiltersOnMount) return;
    dispatch(
      setInitialGenericProducts({
        data: initialData,
        slug,
        type: slugData.type as GenericProductsType,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveIsSuccess = isSuccess && currentSlug === slug;

  // SSR fallback: Redux henüz boşken (ilk server render + hydration öncesi)
  // initialData.items'ı doğrudan render et. Aksi halde SSR HTML'e skeleton
  // yazılır — LCP görseli geç keşfedilir ve Google ürünleri göremez.
  // Client'ta setInitialGenericProducts dispatch'i sonrası Redux devralır.
  const initialItems = initialData?.items ?? [];
  // ISR snapshot'ı FİLTRESİZ base listedir. Hydration'dan sonra URL'de filtre
  // varsa bu fallback kullanılmaz: doğru liste gelene kadar skeleton görünür,
  // yanlış ürünler (ve yanlış ürün sayısı) değil.
  const ssrFallbackItems =
    !hydrated || !hasUrlFiltersOnMount ? initialItems : [];
  const hasSsrFallback = ssrFallbackItems.length > 0;
  // effectiveIsSuccess sonrası daima Redux (boş sonuç = "ürün yok" ekranı);
  // fallback yalnızca Redux henüz dolmamışken (SSR + hydration arası) devrede.
  const displayProducts =
    effectiveIsSuccess || products.length > 0 ? products : ssrFallbackItems;

  const pageTitle = seoTitle || seoCategoryTitle || (section?.title ?? slugData.title ?? slug);
  const totalProducts =
    pagination?.total ||
    (hasSsrFallback ? initialData?.pagination?.total ?? 0 : 0) ||
    products.length ||
    0;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { menu_type: _mt, ...productFilterRequest } = activeFilterRequest;

  useEffect(() => {
    if (!initReady) return;

    const currentSlugVal = slugRef.current;
    if (!currentSlugVal) return;

    const req = activeFilterRequestRef.current;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { menu_type: _mtInner, ...prodReqBase } = req;
    // contextFilterOverride (item_ids/category vb.) her zaman üç API'ye gider —
    // kullanıcı bu bağlam filtrelerini kaldırsa bile sabit tutulur.
    const contextCategory = slugData.item_ids?.length
      ? { context_category: slugData.item_ids }
      : {};
    // contextFilterOverride items/filters'a merge edilmez — kullanıcı selected'ı override eder.
    // context_category sabit olarak gider, category/item_ids kullanıcı seçimine göre req'ten gelir.
    const prodReq = { ...prodReqBase, ...contextCategory };

    // slug geçilir → getFilters.fulfilled serverSelected'ı bu slug'a bağlar.
    // context_category her zaman sabit olarak gider (item_ids'ten türetilir).
    fetchFilters(
      {
        ...req,
        ...(slugData.item_ids?.length
          ? { context_category: slugData.item_ids }
          : {}),
      },
      currentSlugVal,
    );
    // item_ids: kullanıcının seçimine göre req'teki ilgili key'den alınır.
    // item_type = "category" → req.category, "brand" → req.brand vb.
    const currentItemIds = slugData.item_type
      ? ((req as unknown as Record<string, unknown>)[slugData.item_type] as
          | number[]
          | undefined)
      : undefined;
    fetchGeneralFilters({
      seque: slugData.seque || "",
      ...(slugData.item_type ? { item_type: slugData.item_type } : {}),
      ...(currentItemIds?.length ? { item_ids: currentItemIds } : {}),
      ...(slugData.item_ids?.length
        ? { context_category: slugData.item_ids }
        : {}),
    });

    const currentProducts = productsRef.current;
    const hasEnoughForRestore =
      !restoreData || currentProducts.length >= restoreData.count;
    // Array değerlerini sırala → sıra farkından kaynaklanan isSameRequest=false sorununu engelle.
    // Slice de aynı fonksiyonu kullanıyor (lastRequestKey) — iki taraf ayrışmamalı.
    const realKey = buildProductsRequestKey(prodReq);
    const isSameRequest = lastFetchedKeyRef.current === realKey;
    // Redux'taki liste bu filtreyle mi dolduruldu? null = SSR base verisi
    // (yalnızca URL'de filtre yokken yazılır) → yeniden kullanılabilir.
    const cacheMatchesRequest =
      lastRequestKeyRef.current === null ||
      lastRequestKeyRef.current === realKey;

    // hasUrlFiltersOnMount: SSR verisi filtresiz base listedir; URL'de filtre/sort
    // varken bu "SSR zaten var" kısayolu ilk fetch'i yutar ve sıralama uygulanmaz.
    if (ssrHydrated && !hasUrlFiltersOnMount && currentSlugRef.current === currentSlugVal && !initialFetchSkippedRef.current && !restoreData) {
      initialFetchSkippedRef.current = true;
      // SSR verisi base isteğe karşılık gelir — lastFetchedKeyRef'i base key ile seed et.
      // Aksi halde ilk kullanıcı filtre etkileşiminde lastFetchedKeyRef hâlâ null kalır ve
      // aşağıdaki "remount cache guard" (lastFetchedKeyRef===null) yanlışlıkla tetiklenip
      // ürün fetch'ini atlar (ilk seçim çalışmıyor, sonrakiler çalışıyor bug'ı).
      lastFetchedKeyRef.current = realKey;
      return;
    }
    initialFetchSkippedRef.current = true;

    // Remount'ta lastFetchedKeyRef null başlar. Redux'ta bu slug'ın başarılı verisi zaten
    // mevcutsa (ürün detayından geri dönme) gereksiz fetch'i engelle:
    // ilk effect çalışmasında ref'i seed et ve skip et.
    // cacheMatchesRequest şart: cache başka bir filtreyle dolduysa (arama sayfasından
    // dönüş, filtre state'inin sıfırlandığı akışlar) eski liste ekranda kalmamalı.
    if (
      lastFetchedKeyRef.current === null &&
      isSuccessRef.current &&
      currentSlugRef.current === currentSlugVal &&
      currentProducts.length > 0 &&
      hasEnoughForRestore &&
      cacheMatchesRequest
    ) {
      lastFetchedKeyRef.current = realKey;
      return;
    }

    // Guard: fetch already in-flight for this slug — debouncedKey re-triggered by
    // URL sync incrementing userInteractionCount, but realKey may differ slightly.
    // Once lastFetchedKeyRef is seeded, skip any overlapping loading trigger.
    if (
      lastFetchedKeyRef.current !== null &&
      isLoadingRef.current &&
      currentSlugRef.current === currentSlugVal
    ) {
      return;
    }

    if (
      isSuccessRef.current &&
      currentSlugRef.current === currentSlugVal &&
      currentProducts.length > 0 &&
      isSameRequest
    ) {
      return;
    }

    lastFetchedKeyRef.current = realKey;
    setSuppressOverlay(false);
    const restoreCount = restoreData?.count ?? 0;
    fetchProducts({
      type: slugTypeRef.current as GenericProductsType,
      slug: currentSlugVal,
      page: 1,
      per_page: restoreCount > 20 ? restoreCount : 20,
      filters: prodReq,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, initReady]);

  if (isError) {
    return (
      <div className="container-main flex flex-col items-center justify-center py-24 gap-4 text-center">
        <p className="text-gray-500 text-sm">
          Ürünler yüklenirken bir hata oluştu.
        </p>
        <button
          onClick={() =>
            fetchProducts({
              type: slugData.type as GenericProductsType,
              slug,
              page: 1,
              per_page: 20,
              filters: productFilterRequest,
            })
          }
          className="px-5 py-2 rounded-lg bg-main text-white text-sm hover:bg-main/90 transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="container-main">
      {/* JSON-LD kapalı: bu sayfanın BreadcrumbList'ini sunucu basıyor
          (app/(pages)/[slug]/page.tsx) — çift schema olmasın. */}
      <DynamicBreadcrumb title={pageTitle} withJsonLd={false} />
      <ProductsLayout
        header={(onOpenFilter) => (
          <ProductsHeader
            title={pageTitle}
            productCount={
              (effectiveIsSuccess || hasSsrFallback) && totalProducts > 0
                ? totalProducts
                : undefined
            }
            onOpenFilter={onOpenFilter}
          />
        )}
        products={displayProducts}
        hasMore={hasMore}
        isLoading={isLoading && !suppressOverlay}
        isPaginating={isPaginating}
        isSuccess={effectiveIsSuccess || hasSsrFallback}
        onLoadMore={() => fetchMore(productFilterRequest)}
        onClearFilters={handleClearFilters}
      />

      {seoCategoryTitle && seoCategoryText && (
        <div
          className="mt-8 mb-6 rounded-2xl border px-6 pt-1 lg:px-10 lg:pt-3 text-center"
          style={{ backgroundColor: "#f3e5f7", borderColor: "#e8c9f0" }}
        >
          <div
            style={{ height: 200, overflowY: "auto" }}
            className="pr-2 pb-8 scrollbar-thin"
          >
            <div
              className="prose prose-sm max-w-2xl mx-auto text-sm lg:text-base text-gray-600 leading-relaxed text-center seo-category-content"
              dangerouslySetInnerHTML={{ __html: sanitizedSeoCategoryText }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericProductsPage;
